import {
    AiAccountLimits,
    AiAgentCatalogItem,
    AiAgentCatalogItemAvailabilityEnum,
    AiBaseUrl,
    AiConfigurationRequest,
    AiInferenceOperation,
    AiKeyMetadata,
    AiKeyMetadataStatusEnum,
    AiProfile,
    AiTargetType,
} from '../../api';
import isEqual from 'lodash/isEqual';

export type AiEnvironment = 'PRODUCTION' | 'SANDBOX';

export type AiKeyStatus = 'ACTIVE' | 'EXPIRED' | 'REVOKED';

/**
 * Whether a profile would be offered to the client by `GET /support/ai`.
 *
 * `unknown` is not `hidden`: for an AGENT profile the answer depends on the agent catalog, and until that
 * has loaded we do not know (NS-3). Showing "hidden" would tell an operator the profile is unavailable when we
 * simply have not asked yet.
 */
export type AiVisibility =
    | { state: 'available' }
    | { state: 'hidden'; reason: 'account-off' | 'profile-disabled' | 'agent-unavailable' }
    | { state: 'unknown' };

export const EMPTY_CONFIGURATION: AiConfigurationRequest = {
    enabled: false,
    accountLimits: {
        maxBudgetUsd: 25,
        budgetPeriod: 'MONTHLY' as AiAccountLimits['budgetPeriod'],
        requestsPerMinute: 60,
        tokensPerMinute: 60000,
    },
    profiles: [],
};

export function envFromBaseUrl(baseUrl: AiBaseUrl | string | undefined): AiEnvironment | undefined {
    switch (baseUrl) {
        case AiBaseUrl.HttpsAiEnterHealthV1:
            return 'PRODUCTION';
        case AiBaseUrl.HttpsAiSandboxEnterHealthV1:
            return 'SANDBOX';
        default:
            return undefined;
    }
}

export function findAgent(agents: AiAgentCatalogItem[] | undefined, id: string | undefined): AiAgentCatalogItem | undefined {
    return id && agents ? agents.find(a => a.id === id) : undefined;
}

export function isAgentReady(agent: AiAgentCatalogItem | undefined): boolean {
    return agent?.availability === AiAgentCatalogItemAvailabilityEnum.READY;
}

export function profileVisibility(
    profile: AiProfile,
    config: Pick<AiConfigurationRequest, 'enabled'>,
    agents: AiAgentCatalogItem[] | undefined,
): AiVisibility {
    if (!config.enabled) {
        return { state: 'hidden', reason: 'account-off' };
    }
    if (!profile.enabled) {
        return { state: 'hidden', reason: 'profile-disabled' };
    }
    if (profile.targetType === AiTargetType.AGENT) {
        if (!agents) {
            return { state: 'unknown' };
        }
        if (!isAgentReady(findAgent(agents, profile.agentId))) {
            return { state: 'hidden', reason: 'agent-unavailable' };
        }
    }
    return { state: 'available' };
}

/**
 * The profile as the server expects it: a MODEL profile carries no agent, an AGENT profile carries no models
 * and is chat-only, and blank optional limits are absent rather than empty strings or NaN.
 */
export function cleanProfile(profile: AiProfile): AiProfile {
    const out: AiProfile = JSON.parse(JSON.stringify(profile));
    if (out.targetType === AiTargetType.MODEL) {
        delete out.agentId;
    } else {
        delete out.models;
        delete out.defaultModel;
        out.operations = [AiInferenceOperation.CHATCOMPLETIONS];
    }
    const limits = out.keyLimits as unknown as Record<string, unknown>;
    for (const key of ['requestsPerMinute', 'tokensPerMinute']) {
        const v = limits[key];
        if (v === '' || v === null || v === undefined || (typeof v === 'number' && Number.isNaN(v))) {
            delete limits[key];
        }
    }
    if (!out.description) {
        delete out.description;
    }
    return out;
}

export function cleanConfiguration(config: AiConfigurationRequest): AiConfigurationRequest {
    return { ...config, profiles: config.profiles.map(cleanProfile) };
}

/** Profile IDs that were issuing before the save and are not after it (the account switch counts). */
export function stoppedProfiles(
    prev: AiConfigurationRequest | undefined,
    next: AiConfigurationRequest,
): string[] {
    if (!prev || !prev.enabled) {
        return [];
    }
    return prev.profiles
        .filter(p => p.enabled)
        .map(p => p.id)
        .filter(id => !next.enabled || !next.profiles.some(p => p.id === id && p.enabled));
}

export function isDirty(
    draft: AiConfigurationRequest | undefined,
    saved: AiConfigurationRequest | undefined,
): boolean {
    return !isEqual(draft, saved);
}

/** Profile IDs whose models or agent reference the named entry; empty when none. */
export function profilesUsingModel(config: AiConfigurationRequest | undefined, model: string): string[] {
    return (config?.profiles ?? []).filter(p => (p.models ?? []).includes(model)).map(p => p.id);
}

export function profilesUsingAgent(config: AiConfigurationRequest | undefined, agentId: string): string[] {
    return (config?.profiles ?? []).filter(p => p.agentId === agentId).map(p => p.id);
}

/**
 * Status shown for a key. The status we fetched is a snapshot: a key that was ACTIVE when fetched is EXPIRED
 * once `expiresAt` passes, with no further request, so the clock decides that one case. REVOKED is never
 * downgraded.
 */
export function keyStatus(key: Pick<AiKeyMetadata, 'status' | 'expiresAt'>, now: number): AiKeyStatus {
    if (key.status === AiKeyMetadataStatusEnum.REVOKED) {
        return 'REVOKED';
    }
    const expires = Date.parse(key.expiresAt);
    if (key.status === AiKeyMetadataStatusEnum.EXPIRED || (!Number.isNaN(expires) && expires <= now)) {
        return 'EXPIRED';
    }
    return 'ACTIVE';
}

export function minutesLeft(key: Pick<AiKeyMetadata, 'expiresAt'>, now: number): number {
    const expires = Date.parse(key.expiresAt);
    return Number.isNaN(expires) ? 0 : Math.max(0, Math.round((expires - now) / 60000));
}

export function humanSeconds(value: number | string | undefined): string {
    const s = Number(value);
    if (!s || Number.isNaN(s)) {
        return '';
    }
    return s % 60 === 0 ? `${s / 60} min` : `${s} s`;
}

export const OPERATIONS: ReadonlyArray<{ value: AiInferenceOperation; label: string; route: string }> = [
    { value: AiInferenceOperation.CHATCOMPLETIONS, label: 'Chat', route: '/v1/chat/completions' },
    { value: AiInferenceOperation.RESPONSES, label: 'Responses', route: '/v1/responses' },
    { value: AiInferenceOperation.EMBEDDINGS, label: 'Embeddings', route: '/v1/embeddings' },
    { value: AiInferenceOperation.COMPLETIONS, label: 'Completions', route: '/v1/completions' },
];

export const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
